import React from "react";
import { Link } from "react-router-dom";
import siteConfig from "../config/siteConfig";
import "../css/privacy.scss";
import "../css/terms.scss";

const termsSections = [
  ["agreement", "Agreement"],
  ["store", "Store eligibility"],
  ["products", "Products & pricing"],
  ["orders", "Orders & payment"],
  ["shipping", "Shipping & returns"],
  ["conduct", "Acceptable use"],
  ["content", "Intellectual property"],
  ["disclaimers", "Disclaimers"],
  ["liability", "Liability & indemnity"],
  ["general", "General terms"],
];

function Terms() {
  return (
    <main className="policy-page terms">
      <header className="policy-page__header">
        <p className="policy-page__eyebrow">LEGAL / {siteConfig.wordmark}</p>
        <h1>TERMS OF<br />SERVICE</h1>
        <p className="policy-page__lede">
          These terms set the rules for using our store and purchasing products.
          Please read them before placing an order.
        </p>
        <div className="policy-page__meta">
          <span>EFFECTIVE AUGUST 13, 2026</span>
          <span>CONTACT: <a href={`mailto:${siteConfig.contact.email}`}>{siteConfig.contact.email}</a></span>
        </div>
      </header>

      <div className="policy-page__layout">
        <aside className="policy-page__toc">
          <p>ON THIS PAGE</p>
          <nav aria-label="Terms of service sections">
            {termsSections.map(([id, label], index) => (
              <a key={id} href={`#terms-${id}`}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                {label}
              </a>
            ))}
          </nav>
        </aside>

        <article className="policy-page__content">
          <div className="policy-page__intro">
            <p>
              By visiting the {siteConfig.wordmark} store, creating an account,
              contacting support, or purchasing a product, you agree to these
              Terms of Service and the policies linked below.
            </p>
          </div>

          <section id="terms-agreement" className="policy-page__section">
            <span className="policy-page__section-number">01</span>
            <h2>AGREEMENT</h2>
            <p>
              These Terms form a binding agreement between you and
              {` ${siteConfig.brandName}`} for access to this website, store
              features, content, and products (together, the “Services”). If you
              do not agree, do not use the Services.
            </p>
            <p>
              Our <Link to="/privacy">Privacy Policy</Link>, <Link to="/shipping">Shipping Policy</Link>, and <Link to="/returns">Return Policy</Link> form
              part of these Terms. If a product page contains specific terms for
              a preorder, limited release, warranty, or promotion, those terms
              also apply to that product.
            </p>
          </section>

          <section id="terms-store" className="policy-page__section">
            <span className="policy-page__section-number">02</span>
            <h2>STORE ELIGIBILITY</h2>
            <p>
              You must be the age of majority where you live or use the Services
              with permission from a parent or legal guardian. Information you
              provide must be accurate and current. You are responsible for
              activity under your account and for keeping sign-in details secure.
            </p>
            <p>
              Products are offered for personal or household use unless we agree
              otherwise in writing. We may limit quantities, refuse service,
              cancel suspicious activity, or restrict access when reasonably
              necessary to protect customers, the store, or legal rights.
            </p>
          </section>

          <section id="terms-products" className="policy-page__section">
            <span className="policy-page__section-number">03</span>
            <h2>PRODUCTS &amp; PRICING</h2>
            <p>
              We aim to present accurate product descriptions, specifications,
              availability, and color. Screens and lighting vary, so the physical
              product may look slightly different from its image. Minor updates
              that do not materially reduce product function may occur between
              production runs.
            </p>
            <p>
              Prices are displayed in {siteConfig.commerce.currency} unless noted
              otherwise and may change without notice. Taxes, duties, and delivery
              charges are shown where available before an order is submitted. A
              pricing or description error may be corrected, including after an
              order is placed; if the change is material, we will contact you or
              cancel and refund the affected item.
            </p>
          </section>

          <section id="terms-orders" className="policy-page__section">
            <span className="policy-page__section-number">04</span>
            <h2>ORDERS &amp; PAYMENT</h2>
            <p>
              Your checkout submission is an offer to purchase. An automated
              confirmation means we received the order, not that we accepted it.
              Acceptance occurs when the order is prepared for shipment or when
              we otherwise confirm acceptance.
            </p>
            <ul>
              <li>You authorize the displayed total and any required tax or delivery charge.</li>
              <li>Payment information must belong to you or be used with the owner’s permission.</li>
              <li>Orders may be held or canceled for failed payment, inventory error, fraud review, geographic restriction, or violation of these Terms.</li>
              <li>Approved refunds are returned to the original payment method unless law requires another method.</li>
            </ul>
          </section>

          <section id="terms-shipping" className="policy-page__section">
            <span className="policy-page__section-number">05</span>
            <h2>SHIPPING &amp; RETURNS</h2>
            <p>
              Processing and transit dates are estimates, not guarantees. Risk of
              loss and title transfer as required by applicable law and the
              selected delivery method. You are responsible for providing a safe,
              complete delivery address and reviewing tracking updates.
            </p>
            <p>
              Return eligibility, exclusions, and refund timing are described in
              our <Link to="/returns">Return Policy</Link>. Delivery regions,
              customs information, and estimated transit windows are in our <Link to="/shipping">Shipping Policy</Link>.
            </p>
          </section>

          <section id="terms-conduct" className="policy-page__section">
            <span className="policy-page__section-number">06</span>
            <h2>ACCEPTABLE USE</h2>
            <p>You may not use the Services to:</p>
            <ul>
              <li>break a law, infringe rights, harass others, or submit harmful or deceptive material;</li>
              <li>interfere with site security, checkout, networks, or another person’s access;</li>
              <li>introduce malicious code, scrape the store at unreasonable volume, or bypass access controls;</li>
              <li>impersonate another person, submit fraudulent orders, or misuse promotions; or</li>
              <li>copy, resell, or commercially exploit the Services without written permission.</li>
            </ul>
          </section>

          <section id="terms-content" className="policy-page__section">
            <span className="policy-page__section-number">07</span>
            <h2>INTELLECTUAL PROPERTY</h2>
            <p>
              Store text, product designs, photography, graphics, interfaces,
              software, and other content are owned by us or licensed to us and
              are protected by intellectual-property laws. You receive a limited,
              personal, revocable right to use the Services for shopping and
              product support.
            </p>
            <p>
              If you submit a review, image, or other content, you confirm you
              have the right to share it. You grant us a non-exclusive, worldwide,
              royalty-free license to host, reproduce, and display that content
              for operating, promoting, and improving the Services, subject to our
              Privacy Policy.
            </p>
          </section>

          <section id="terms-disclaimers" className="policy-page__section">
            <span className="policy-page__section-number">08</span>
            <h2>DISCLAIMERS</h2>
            <p>
              To the fullest extent allowed by law, the Services are provided “as
              is” and “as available.” We do not promise uninterrupted access,
              error-free content, or compatibility with every device or setup.
              Product warranties, when offered, are limited to the terms shown
              with the applicable product.
            </p>
            <div className="policy-page__note">
              <strong>CONSUMER RIGHTS REMAIN</strong>
              <p>
                Nothing in these Terms excludes a guarantee, remedy, or liability
                that cannot legally be excluded in your location.
              </p>
            </div>
          </section>

          <section id="terms-liability" className="policy-page__section">
            <span className="policy-page__section-number">09</span>
            <h2>LIABILITY &amp; INDEMNITY</h2>
            <p>
              To the fullest extent allowed by law, neither we nor our service
              providers are liable for indirect, incidental, special,
              consequential, or punitive loss arising from the Services or
              products. Our aggregate liability for a claim will not exceed the
              amount you paid for the product or service giving rise to that
              claim during the preceding twelve months.
            </p>
            <p>
              You agree to be responsible for reasonable losses and costs caused
              by your unlawful misuse of the Services, violation of these Terms,
              or infringement of another person’s rights.
            </p>
          </section>

          <section id="terms-general" className="policy-page__section">
            <span className="policy-page__section-number">10</span>
            <h2>GENERAL TERMS</h2>
            <p>
              These Terms are governed by the laws applicable in Hong Kong,
              without regard to conflict-of-law rules, while any
              mandatory consumer protections in your home jurisdiction continue
              to apply. Before beginning a formal dispute, contact us so both
              sides can attempt a good-faith resolution.
            </p>
            <p>
              If one provision is unenforceable, the remaining provisions stay in
              effect. Our failure to enforce a provision is not a waiver. We may
              update these Terms; continued use after the effective date means you
              accept the revised version.
            </p>
            <p>
              Questions can be sent to <a href={`mailto:${siteConfig.contact.email}`}>{siteConfig.contact.email}</a> or through
              our <Link to="/contact">contact page</Link>.
            </p>
          </section>

          <nav className="policy-page__related" aria-label="Related policies">
            <span>RELATED</span>
            <Link to="/privacy">PRIVACY</Link>
            <Link to="/returns">RETURNS</Link>
            <Link to="/shipping">SHIPPING</Link>
          </nav>
        </article>
      </div>
    </main>
  );
}

export default Terms;
